import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Headphones } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { setSupportOffDuty } from '@/api/tickets.api';
import { useAuthStore } from '@/store/authStore';

/**
 * P6-12 / M11 — operator off-duty toggle in TopBar.
 *
 * Only renders for SUPPORT and SUPER_ADMIN roles since they're the
 * ones in the round-robin pool. The local optimistic flip happens
 * before the request returns so the UI stays snappy; on failure we
 * roll back and surface a tiny error.
 */

const SUPPORT_ROLES = ['SUPPORT', 'SUPER_ADMIN'];

export function SupportAvailabilityToggle(): JSX.Element | null {
    const { t } = useTranslation();
    const role = useAuthStore((s) => s.user?.role);
    const [offDuty, setOff] = useState(false);
    const [busy, setBusy] = useState(false);

    if (!role || !SUPPORT_ROLES.includes(role)) return null;

    const onToggle = async (): Promise<void> => {
        if (busy) return;
        const next = !offDuty;
        setOff(next);
        setBusy(true);
        try {
            await setSupportOffDuty(next);
        } catch {
            // Revert on failure — keep UI honest about server state.
            setOff(!next);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Button
            size="icon"
            variant="ghost"
            onClick={onToggle}
            disabled={busy}
            aria-pressed={!offDuty}
            aria-label={
                offDuty
                    ? t('tickets.availability.offDuty', 'Off duty — click to go on')
                    : t('tickets.availability.onDuty', 'On duty — click to go off')
            }
            title={
                offDuty
                    ? t('tickets.availability.offDuty', 'Off duty — click to go on')
                    : t('tickets.availability.onDuty', 'On duty — click to go off')
            }
        >
            <Headphones className={`h-4 w-4 ${offDuty ? 'opacity-30' : ''}`} />
        </Button>
    );
}
